import { Component, Input, Inject, NgZone, OnChanges, OnDestroy, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { ChartLoadingComponent } from '../chart-loading/chart-loading.component';
import { HEX_COLOR_PALETTE } from '../utils/color-palette';
import { valueFormatter } from '../utils/chart-utils';

export interface ChartLineSeries {
  field: string;
  name: string;
  color: string;
  dashed?: boolean;
  type?: 'default' | 'money';
}

export interface ChartLineGroupBy {
  key: string;
  valueField: string;
  type?: 'default' | 'money';
}

export interface ChartLineConfig {
  categoryField: string;
  series?: ChartLineSeries[];
  groupBy?: ChartLineGroupBy;
  data ?: any[];
  title?: string;
  col?: number;                                  // md:col-{n} en ChartLineMultipleComponent — 1 a 12, default 12
}

export interface ChartLineCardConfig {
  title: string;
  sub?: string;
  chart: ChartLineConfig;
}

@Component({
  selector: 'app-chart-line',
  standalone: true,
  imports: [CommonModule, ChartLoadingComponent],
  styles: `  
    :host {
      display: block;
      height: 100%;
    }

    .line-canvas {
      width: 100%;
      height: 260px;
    }
  `,
  template: `
    <app-chart-loading *ngIf="isLoading; else canvas"></app-chart-loading>
    <ng-template #canvas>
      <div [id]="chartId" class="line-canvas"></div>
    </ng-template>
  `
})
export class ChartLineComponent implements OnChanges, OnDestroy {

  chartId !: string;
  @Input() config!: ChartLineConfig;
  @Input() data: any[] = [];

  private root?: am5.Root;
  isLoading = true;

  private xAxis?: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private lineSeriesList: am5xy.LineSeries[] = [];
  private readonly palette = HEX_COLOR_PALETTE;
  private resolvedSeries: ChartLineSeries[] = [];
  private resolvedData: any[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.chartId = `chart-line-${crypto.randomUUID()}`;
  }

  private initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      this.root = root;

      const textColor = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);
      const gridColor = am5.color(0x33506f);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingLeft: 0,
        })
      );

      // X axis
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: this.config.categoryField,
          renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 25 }),
        })
      );
      xAxis.get('renderer').labels.template.setAll({
        fill: mutedColor,
        fontSize: 11,
        rotation: -16,
        centerX: am5.percent(100),
        centerY: am5.percent(50),
      });
      xAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });
      this.xAxis = xAxis;

      // Y axis
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
        })
      );
      yAxis.get('renderer').labels.template.setAll({ fill: mutedColor, fontSize: 11 });
      yAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });

      // Line series
      for (const s of this.resolvedSeries) {
        const lineColor = am5.color(s.color);

        const tooltip = am5.Tooltip.new(root, {});
        tooltip.label.adapters.add('text', (_text, target) => {
          const dataItem = target.dataItem as any;
          if (!dataItem) return _text;
          const categoryX = dataItem.get('categoryX');
          const value = dataItem.get('valueY') as number | undefined;
          const formattedValue = value == null ? '-' : valueFormatter(s.type, value);
          return `[bold]${s.name}[/]\n${categoryX}: ${formattedValue}`;
        });

        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: s.name,
            xAxis,
            yAxis,
            categoryXField: this.config.categoryField,
            valueYField: s.field,
            stroke: lineColor,
            connect: false,
            tooltip,
          })
        );

        series.strokes.template.setAll({
          strokeWidth: 3,
          strokeDasharray: s.dashed ? [5, 4] : [],
        });

        series.bullets.push(() =>
          am5.Bullet.new(root, {
            sprite: am5.Circle.new(root, { radius: 4, fill: lineColor }),
          })
        );

        series.appear(500);
        this.lineSeriesList.push(series);
      }

      // Legend
      const legend = chart.children.push(
        am5.Legend.new(root, {
          x: am5.percent(50),
          centerX: am5.percent(50),
          y: 0,
          centerY: 0,
        })
      );
      legend.labels.template.setAll({
        fill: textColor,
        fontSize: 12,
        maxWidth: 140,
        oversizedBehavior: 'truncate',
        tooltipText: '{name}',
      });
      legend.valueLabels.template.setAll({ forceHidden: true });
      legend.data.setAll(chart.series.values);

      chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
      chart.appear(500, 100);
    });
  }

  ngOnChanges(): void {
    if (!this.config || !this.data?.length) return;

    const resolved = this.resolve();
    this.resolvedSeries = resolved.series;
    this.resolvedData = resolved.data;

    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    this.xAxis?.data.setAll(this.resolvedData);
    this.lineSeriesList.forEach(s => s.data.setAll(this.resolvedData));
  }

  /** series estáticas si vienen declaradas; si viene groupBy, se derivan de los valores únicos de esa columna */
  private resolve(): { series: ChartLineSeries[]; data: any[] } {
    if (!this.config.groupBy) return { series: this.config.series ?? [], data: this.data };

    const { key, valueField, type } = this.config.groupBy;
    const categoryField = this.config.categoryField;
    const rowsByCategory = new Map<string, any>();
    const groupValues = new Set<string>();
    this.data.forEach(row => {
      const category = row[categoryField];
      const groupValue = row[key];
      groupValues.add(groupValue);
      if (!rowsByCategory.has(category)) rowsByCategory.set(category, { [categoryField]: category });
      rowsByCategory.get(category)[groupValue] = row[valueField];
    });
    const series: ChartLineSeries[] = [...groupValues].map((name, index) => ({
      field: name,
      name,
      color: this.palette[index % this.palette.length],
      type,
    }));
    return { series, data: [...rowsByCategory.values()] };
  }

  ngOnDestroy(): void {
    this.lineSeriesList = [];
    this.root?.dispose();
  }
}
